import type { Workflow } from '@compass/client/types/workflow';

import { WorkflowType } from '@/constants/dispositions';
import { SUMMARY_EDITABLE_WORKFLOW_TYPES } from '@/constants/summary';

/**
 * Whether a workflow is internal (backend-only) and should be hidden from the
 * user dashboard: the workflow switcher, the manufacturer picker, and program
 * auto-selection. Driven by the explicit `internal` flag set by admins, which
 * replaces the previous "has an input schema" heuristic.
 */
export const isInternalWorkflow = (workflow: Workflow): boolean => workflow.internal === true;

/**
 * Whether a workflow should be visible to users in the dashboard.
 * The inverse of {@link isInternalWorkflow}.
 */
export const isUserVisibleWorkflow = (workflow: Workflow): boolean => !isInternalWorkflow(workflow);

/**
 * Check if the workflow type is recall scheduler.
 * @param workflowType - The workflow type to check
 * @returns True if the workflow type is recall scheduler
 */
export const isRecallSchedulerWorkflow = (workflowType?: string): boolean =>
  workflowType === WorkflowType.RECALL_SCHEDULER;

/**
 * Check if the workflow type supports summary editing and regeneration.
 * @param workflowType - The workflow type to check
 * @returns True if the workflow type supports summary editing
 */
/**
 * Returns true when a link confirmation dialog should be shown before linking
 * a call to a case. Skipped only when the program has exactly one PQC workflow.
 */
export const shouldShowLinkConfirmation = (workflows: Workflow[]): boolean =>
  workflows.filter(w => w.workflow_type === WorkflowType.PQC).length !== 1;

export const isSummaryEditableWorkflow = (workflowType?: string): boolean => {
  if (!workflowType) return false;
  return SUMMARY_EDITABLE_WORKFLOW_TYPES.includes(workflowType as WorkflowType);
};
